import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AnimationOptions } from 'ngx-lottie';
import { TranslateService } from '@ngx-translate/core';
import { AdmissionService } from '../admission/admission.service';
import { commonFunctionService } from '../utils/commonFunctions';
import * as successAnimationData from '../../assets/animation/success_animation.json';
import parsePhoneNumberFromString from 'libphonenumber-js/max';
import { BrandService } from '../brand/brand.service';

declare var $: any;
declare var customToastr: any;

type EnquiryStatusSlug = 'NEW' | 'IN_REVIEW' | 'CONFIRMED' | 'WAITLISTED' | 'LEFT' | 'REJECTED' | 'CONVERTED';

interface AdmissionEnquiry {
  enquiryId: number;
  studentName: string;
  studentImage: string;
  gender: string;
  dob: string;
  parentName: string;
  parentPhone: string;
  className: string;
  section: string;
  type: 'Fresh' | 'Transfer';
  enquiryDate: string;
  statusSlug: EnquiryStatusSlug;
  statusName: string;
  raw: any;
}

type StatusFilter = 'All' | EnquiryStatusSlug;

const STATUS_FILTERS: { slug: StatusFilter; label: string; statusId: number | null }[] = [
  { slug: 'All', label: 'All', statusId: null },
  { slug: 'NEW', label: 'New', statusId: 1 },
  { slug: 'IN_REVIEW', label: 'In Review', statusId: 2 },
  { slug: 'CONFIRMED', label: 'Confirmed', statusId: 3 },
  { slug: 'WAITLISTED', label: 'Waitlisted', statusId: 4 },
  { slug: 'LEFT', label: 'Left', statusId: 5 },
  { slug: 'REJECTED', label: 'Rejected', statusId: 6 },
  { slug: 'CONVERTED', label: 'Converted', statusId: 7 },
];

interface StatConfig {
  label: string;
  icon: string;
  variant: string;
  isPercent?: boolean;
}

const STAT_SLUG_CONFIG: Record<string, StatConfig> = {
  TOTAL_ENQUIRIES: { label: 'Total enquiries', icon: 'ri-file-text-line', variant: 'stat-total' },
  CONVERSION_RATE: { label: 'Conversion rate', icon: 'ri-line-chart-line', variant: 'stat-admitted', isPercent: true },
  PENDING_ENQUIRIES: { label: 'Pending enquiries', icon: 'ri-time-line', variant: 'stat-pending' },
  DROP_OFF_RATE: { label: 'Drop-off rate', icon: 'ri-arrow-down-circle-line', variant: 'stat-rejected', isPercent: true },
  MOST_ENQUIRED_CLASS: { label: 'Most enquired class', icon: 'ri-graduation-cap-line', variant: 'stat-in-review' },
};

interface DisplayStat {
  slug: string;
  label: string;
  icon: string;
  variant: string;
  value: string;
  isText: boolean;
}

interface ActivityChange {
  fieldSlug: string;
  fieldName: string;
  oldValue: string | null;
  newValue: string | null;
  oldValueDocumentType: string | null;
  newValueDocumentType: string | null;
}

interface ActivityLogEntry {
  pkActivityId: number;
  action: 'CREATED' | 'STATUS_CHANGED' | 'EDITED' | 'CONVERTED' | string;
  remarks: string;
  performedBy: { id: number; type: string; name: string; image: string };
  changes: ActivityChange[];
  date: string;
  time: string;
}

interface NewEnquiryForm {
  firstName: string;
  lastName: string;
  dob: Date | null;
  gender: 'Male' | 'Female';
  academicYearId: number | null;
  categoryId: number | null;
  classId: number | null;
  sectionId: number | null;
  admissionType: 'Fresh' | 'Transfer';
  previousSchoolName: string;
  board: string;
  previousClassStudied: string;
  previousClassResult: string;
  reasonForTransfer: string;
  previousSchoolAddress: string;
  tcNumber: string;
  tcDate: Date | null;
  fatherName: string;
  fatherMobile: string;
  fatherEmail: string;
  fatherAddress: string;
  motherName: string;
  motherMobile: string;
  motherEmail: string;
  motherAddress: string;
  statusId: number | null;
}

interface NewEnquiryFieldErrors {
  firstName?: string;
  dob?: string;
  gender?: string;
  academicYearId?: string;
  categoryId?: string;
  classId?: string;
  fatherName?: string;
  fatherMobile?: string;
  fatherEmail?: string;
  fatherAddress?: string;
  motherMobile?: string;
}

@Component({
  selector: 'app-admission-inquiry',
  templateUrl: './admission-inquiry.component.html',
  styleUrls: [
    './../../assets/css/theme-default/bootstrap.css',
    './../../assets/css/theme-default/materialadmin.css',
    './../../assets/css/theme-default/font-awesome.min.css',
    './../../assets/css/theme-default/material-design-iconic-font.min.css',
    './admission-inquiry.component.css'],
  encapsulation: ViewEncapsulation.None,
  providers: [AdmissionService]
})
export class AdmissionInquiryComponent implements OnInit {

  brand = this.brandService.brand;

  constructor(private admissionService: AdmissionService, private sanitizer: DomSanitizer, private translate: TranslateService,
    private commonFunction: commonFunctionService, private brandService: BrandService) { }

  handleMobileKeyDown(event: KeyboardEvent): void {
    this.commonFunction.handleKeyDown(event);
  }

  get uploadTcLabel(): string {
    return this.translate.instant('admissionEnquiryPage.uploadTc');
  }

  get tcDocumentLabel(): string {
    return this.translate.instant('admissionEnquiryPage.tcDocument');
  }

  academicYears: { id: number; label: string; isCurrent: boolean }[] = [];
  selectedAcademicYearId: number | null = null;

  categories: { id: number; label: string }[] = [];
  filteredClasses: { id: number; label: string }[] = [];
  filteredSections: { id: number; label: string }[] = [];
  enquiryStatuses: { id: number; label: string }[] = [];

  loadingAcademicYears: boolean = false;
  loadingCategories: boolean = false;
  loadingClasses: boolean = false;
  loadingSections: boolean = false;
  loadingEnquiryStatuses: boolean = false;

  loadEnquiryStatuses(preserveStatusId?: number | null): void {
    this.loadingEnquiryStatuses = true;
    this.admissionService.getAdmissionEnquiryStatuses().subscribe({
      next: (response: any) => {
        const list = response?.body?.statuses ?? response?.body ?? [];
        this.enquiryStatuses = (Array.isArray(list) ? list : []).map((item: any) => ({
          id: item.statusId,
          label: item.statusName,
        }));
        this.loadingEnquiryStatuses = false;
        if (preserveStatusId != null) {
          this.newEnquiryForm.statusId = preserveStatusId;
        }
      },
      error: () => {
        this.loadingEnquiryStatuses = false;
      },
    });
  }

  loadAcademicYears(): void {
    this.loadingAcademicYears = true;
    this.admissionService.getPastCurrentAcademicList().subscribe({
      next: (response: any) => {
        const list = response?.body?.currentAndPastAcademicData ?? [];
        this.academicYears = list.map((year: any) => ({
          id: year.pkAcademicId,
          label: year.Year,
          isCurrent: !!year.isCurrentAcademic,
        }));
        const currentYear = this.academicYears.find(year => year.isCurrent);
        this.selectedAcademicYearId = currentYear ? currentYear.id : (this.academicYears[0]?.id ?? null);
        this.newEnquiryForm.academicYearId = this.selectedAcademicYearId;
        this.loadingAcademicYears = false;
        this.loadEnquiries();
      },
      error: () => {
        this.loadingAcademicYears = false;
      },
    });
  }

  loadCategories(): void {
    this.loadingCategories = true;
    this.admissionService.getAdmissionEnquiryCategories().subscribe({
      next: (response: any) => {
        const list = response?.body?.categories ?? [];
        this.categories = list.map((item: any) => ({
          id: item.categoryId,
          label: item.categoryName,
        }));
        this.loadingCategories = false;
      },
      error: () => {
        this.loadingCategories = false;
      },
    });
  }

  loadClassStandards(categoryId: number): void {
    this.loadingClasses = true;
    this.admissionService.getAdmissionEnquiryClassStandards([categoryId]).subscribe({
      next: (response: any) => {
        const groups = response?.body?.classStandards ?? [];
        this.filteredClasses = groups.flatMap((group: any) =>
          (group.standards ?? []).map((standard: any) => ({
            id: standard.classStandardsId,
            label: standard.classStandardName,
          }))
        );
        this.loadingClasses = false;
      },
      error: () => {
        this.loadingClasses = false;
      },
    });
  }

  loadClassSections(categoryId: number, classStandardId: number): void {
    this.loadingSections = true;
    this.admissionService.getAdmissionEnquiryClassSections([categoryId], [classStandardId]).subscribe({
      next: (response: any) => {
        const categoryGroups = response?.body?.classSections ?? [];
        this.filteredSections = categoryGroups.flatMap((categoryGroup: any) =>
          (categoryGroup.standards ?? []).flatMap((standard: any) =>
            (standard.sections ?? []).map((section: any) => ({
              id: section.classId,
              label: section.classDivision,
            }))
          )
        );
        this.loadingSections = false;
      },
      error: () => {
        this.loadingSections = false;
      },
    });
  }

  onEnquiryCategoryChange(): void {
    this.newEnquiryForm.classId = null;
    this.newEnquiryForm.sectionId = null;
    this.filteredClasses = [];
    this.filteredSections = [];
    if (this.newEnquiryForm.categoryId) {
      this.loadClassStandards(this.newEnquiryForm.categoryId);
    }
  }

  onEnquiryClassChange(): void {
    this.newEnquiryForm.sectionId = null;
    this.filteredSections = [];
    if (this.newEnquiryForm.categoryId && this.newEnquiryForm.classId) {
      this.loadClassSections(this.newEnquiryForm.categoryId, this.newEnquiryForm.classId);
    }
  }

  today: Date = new Date();

  showNewEnquiryModal: boolean = false;
  isSubmittingEnquiry: boolean = false;
  fieldErrors: NewEnquiryFieldErrors = {};

  isEditMode: boolean = false;
  editingEnquiryId: number | null = null;

  showViewEnquiryPanel: boolean = false;
  viewingEnquiry: AdmissionEnquiry | null = null;

  openViewEnquiryPanel(enquiry: AdmissionEnquiry): void {
    this.viewingEnquiry = enquiry;
    this.showViewEnquiryPanel = true;
  }

  closeViewEnquiryPanel(): void {
    this.showViewEnquiryPanel = false;
    this.viewingEnquiry = null;
  }

  private escapeHtml(value: string | null | undefined): string {
    if (!value) {
      return '—';
    }
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  printEnquiryDetails(): void {
    const enquiry = this.viewingEnquiry;
    if (!enquiry) {
      return;
    }
    const raw = enquiry.raw || {};
    const esc = (v: any) => this.escapeHtml(v);
    const schoolInfo = localStorage.getItem('schoolAllInfo');
    const parsedSchoolInfo = schoolInfo ? JSON.parse(schoolInfo) : {};
    const schoolName = parsedSchoolInfo.schoolName || '';

    const isTransfer = enquiry.type === 'Transfer';
    const hasMother = !!raw.motherName;

    const section = (title: string, rows: Array<[string, any]>) => `
      <div class="section">
        <h3>${esc(title)}</h3>
        <table>
          ${rows.map(([label, value]) => `
            <tr><td class="label">${esc(label)}</td><td class="value">${esc(value)}</td></tr>
          `).join('')}
        </table>
      </div>
    `;

    const transferSection = isTransfer ? section('Transfer / Previous School', [
      ['Previous School', raw.previousSchoolName],
      ['Board', raw.previousSchoolBoard],
      ['Previous Class', raw.previousClassStudied],
      ['Result', raw.previousClassResult],
      ['Reason for Transfer', raw.reasonForTransfer],
      ['TC Number', raw.tcNumber],
      ['Previous School Address', raw.previousSchoolAddress],
    ]) : '';

    const motherSection = hasMother ? section('Mother', [
      ['Name', raw.motherName],
      ['Mobile', raw.motherMobileNumber],
      ['Email', raw.motherEmailId],
      ['Address', raw.motherAddress],
    ]) : '';

    const photoHtml = enquiry.studentImage
      ? `<img class="student-photo" src="${esc(enquiry.studentImage)}" alt="" onerror="this.style.display='none'" />`
      : `<div class="student-photo student-photo-fallback">${esc(this.getInitials(enquiry.studentName))}</div>`;

    const html = `
      <html>
        <head>
          <title>${esc(schoolName)} - Enquiry Details</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 24px; color: #222; }
            .report-header { text-align: center; margin-bottom: 20px; }
            .report-header h2 { margin: 0; font-size: 20px; }
            .student-photo { display: block; margin: 0 auto 10px; width: 90px; height: 90px; border-radius: 50%; object-fit: cover; border: 1px solid #ddd; }
            .student-photo-fallback { display: flex; align-items: center; justify-content: center; background: var(--select-bg-color); color: #fff; font-size: 28px; font-weight: 700; }
            .student-name { text-align: center; font-size: 18px; font-weight: bold; margin: 8px 0 2px; }
            .status { text-align: center; font-size: 13px; color: #555; margin-bottom: 20px; }
            .section { margin-bottom: 18px; }
            .section h3 { font-size: 14px; border-bottom: 1px solid #ccc; padding-bottom: 4px; margin-bottom: 8px; }
            table { border-collapse: collapse; width: 100%; font-size: 13px; }
            td { padding: 5px 6px; vertical-align: top; }
            td.label { width: 220px; color: #666; }
            td.value { font-weight: 600; }
            @media print { @page { margin: 12mm; } }
          </style>
        </head>
        <body>
          <div class="report-header"><h2>${esc(schoolName)}</h2></div>
          ${photoHtml}
          <div class="student-name">${esc(enquiry.studentName)}</div>
          <div class="status">${esc(enquiry.statusName)}</div>

          ${section('Student Details', [
            ['Pre-Registration No.', raw.preRegistrationNumber],
            ['Category', raw.categoryName],
            ['Class / Section', `${enquiry.className || ''}${enquiry.section ? ' · ' + enquiry.section : ''}`],
            ['Admission Type', enquiry.type],
            ['Enquiry Date', enquiry.enquiryDate],
          ])}

          ${transferSection}

          ${section('Father', [
            ['Name', raw.fatherName],
            ['Mobile', enquiry.parentPhone],
            ['Email', raw.fatherEmailId],
            ['Address', raw.fatherAddress],
          ])}

          ${motherSection}

          <script>
            window.addEventListener('afterprint', function () {
              window.close();
            });
            function triggerPrint() {
              window.focus();
              window.print();
            }
            var img = document.querySelector('.student-photo');
            if (img && img.tagName === 'IMG' && !img.complete) {
              img.addEventListener('load', triggerPrint);
              img.addEventListener('error', triggerPrint);
              setTimeout(triggerPrint, 2000);
            } else {
              window.onload = triggerPrint;
            }
          </script>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      customToastr('Please allow pop-ups to preview and print.', 'error');
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
  }

  showHistoryPanel: boolean = false;
  loadingHistory: boolean = false;
  historyLog: ActivityLogEntry[] = [];

  openHistoryPanel(): void {
    if (!this.viewingEnquiry || !this.selectedAcademicYearId) {
      return;
    }
    this.loadingHistory = true;
    this.showHistoryPanel = true;
    this.admissionService.getAdmissionEnquiryDetail(this.selectedAcademicYearId, this.viewingEnquiry.enquiryId).subscribe({
      next: (response: any) => {
        this.historyLog = (response?.body?.activityLog ?? []).slice().reverse();
        this.loadingHistory = false;
      },
      error: () => {
        this.historyLog = [];
        this.loadingHistory = false;
        customToastr(this.translate.instant('admissionEnquiryPage.failedToLoadHistory'), 'error');
      },
    });
  }

  closeHistoryPanel(): void {
    this.showHistoryPanel = false;
    this.historyLog = [];
  }

  backToViewFromHistory(): void {
    this.closeHistoryPanel();
  }

  getActivityTitle(entry: ActivityLogEntry): string {
    if (entry.action === 'CREATED') {
      return this.translate.instant('admissionEnquiryPage.activityEnquiryCreated');
    }
    if (entry.action === 'CONVERTED') {
      return this.translate.instant('admissionEnquiryPage.activityEnquiryConverted');
    }
    if (entry.action === 'STATUS_CHANGED') {
      return this.translate.instant('admissionEnquiryPage.activityStatusChanged');
    }
    if (entry.changes.length === 1) {
      const field = entry.changes[0].fieldName;
      if (field === 'TC Document') {
        return this.translate.instant('admissionEnquiryPage.activityTcDocumentUpdated');
      }
      // `field` is backend-driven (activity log fieldName) — must stay as-is.
      return `${field} ${this.translate.instant('admissionEnquiryPage.activityFieldUpdated')}`;
    }
    return this.translate.instant('admissionEnquiryPage.activityEnquiryUpdated');
  }

  getActivityDotClass(isFirst: boolean): string {
    return isFirst ? 'history-dot history-dot-filled' : 'history-dot';
  }

  getNonStatusChanges(entry: ActivityLogEntry): ActivityChange[] {
    return entry.changes.filter(c => c.fieldSlug !== 'fkStatusId');
  }

  getStatusChange(entry: ActivityLogEntry): ActivityChange | undefined {
    return entry.changes.find(c => c.fieldSlug === 'fkStatusId');
  }

  formatHistoryValue(value: string | null | undefined): string {
    if (!value) {
      return value as string;
    }
    if (value === 'N/A' || /\d/.test(value)) {
      return value;
    }
    if (value === value.toUpperCase() && /^[A-Z_\s]+$/.test(value)) {
      return value.toLowerCase().replace(/(^|\s)\S/g, ch => ch.toUpperCase());
    }
    return value;
  }

  isDocumentChange(change: ActivityChange): boolean {
    return change.newValueDocumentType === 'image' || change.newValueDocumentType === 'document'
      || change.oldValueDocumentType === 'image' || change.oldValueDocumentType === 'document';
  }

  hasDocumentValue(value: string | null | undefined): boolean {
    return !!value && value !== 'N/A';
  }

  editFromViewPanel(): void {
    if (!this.viewingEnquiry) {
      return;
    }
    const enquiry = this.viewingEnquiry;
    this.closeViewEnquiryPanel();
    this.openEditEnquiryPanel(enquiry);
  }

  showAdmitModal: boolean = false;
  isSubmittingAdmit: boolean = false;
  admittingEnquiry: AdmissionEnquiry | null = null;
  admitForm: {
    registrationId: string;
    dateOfAdmission: Date | null;
    admissionStream: string;
    categoryId: number | null;
    classId: number | null;
    sectionId: number | null;
  } = {
    registrationId: '',
    dateOfAdmission: null,
    admissionStream: '',
    categoryId: null,
    classId: null,
    sectionId: null,
  };
  admitFieldErrors: { registrationId?: string; dateOfAdmission?: string; categoryId?: string; classId?: string; sectionId?: string } = {};
  lastRegistrationId: string = '';
  admissionStreamOptions: { id: string; label: string }[] = [];
  loadingAdmissionStreams: boolean = false;
  admitCategories: { id: number; label: string }[] = [];
  admitClasses: { id: number; label: string }[] = [];
  admitSections: { id: number; label: string }[] = [];
  loadingAdmitCategories: boolean = false;
  loadingAdmitClasses: boolean = false;
  loadingAdmitSections: boolean = false;

  openAdmitModal(enquiry: AdmissionEnquiry | null): void {
    if (!enquiry) {
      return;
    }
    this.admittingEnquiry = enquiry;
    this.admitForm = {
      registrationId: '',
      dateOfAdmission: null,
      admissionStream: 'general',
      categoryId: enquiry.raw?.categoryId ?? null,
      classId: enquiry.raw?.classStandardsId ?? null,
      sectionId: enquiry.raw?.classId ?? null,
    };
    this.admitFieldErrors = {};
    this.admitClasses = [];
    this.admitSections = [];
    this.lastRegistrationId = '';
    this.showAdmitModal = true;
    this.showAdmitConfirm = false;
    this.admissionService.getLastRegId(localStorage.getItem('usertoken') || '').subscribe({
      next: (data: any) => {
        this.lastRegistrationId = data?.registrationId || '';
      },
      error: () => {
        this.lastRegistrationId = '';
      },
    });
    this.loadingAdmissionStreams = true;
    this.admissionService.getStudentCategoriesFoAdmission(localStorage.getItem('usertoken') || '').subscribe({
      next: (data: any) => {
        const list = Array.isArray(data) ? data : [];
        this.admissionStreamOptions = list.map((item: any) => ({
          id: `${item.pkStudentsCategoryId}`,
          label: item.Name,
        }));
        this.loadingAdmissionStreams = false;
      },
      error: () => {
        this.admissionStreamOptions = [];
        this.loadingAdmissionStreams = false;
      },
    });
    this.loadingAdmitCategories = true;
    this.admissionService.getAdmissionEnquiryCategories().subscribe({
      next: (response: any) => {
        const list = response?.body?.categories ?? [];
        this.admitCategories = list.map((item: any) => ({
          id: item.categoryId,
          label: item.categoryName,
        }));
        this.loadingAdmitCategories = false;
        if (this.admitForm.categoryId) {
          this.onAdmitCategoryChange(true);
        }
      },
      error: () => {
        this.loadingAdmitCategories = false;
      },
    });
  }

  closeAdmitModal(): void {
    this.showAdmitModal = false;
    this.showAdmitConfirm = false;
    this.admittingEnquiry = null;
  }

  onAdmitCategoryChange(preserveSelection: boolean = false): void {
    const preservedClassId = preserveSelection ? this.admitForm.classId : null;
    const preservedSectionId = preserveSelection ? this.admitForm.sectionId : null;
    if (!preserveSelection) {
      this.admitForm.classId = null;
      this.admitForm.sectionId = null;
    }
    this.admitClasses = [];
    this.admitSections = [];
    if (!this.admitForm.categoryId) {
      return;
    }
    this.loadingAdmitClasses = true;
    this.admissionService.getAdmissionEnquiryClassStandards([this.admitForm.categoryId]).subscribe({
      next: (response: any) => {
        const groups = response?.body?.classStandards ?? [];
        this.admitClasses = groups.flatMap((group: any) =>
          (group.standards ?? []).map((standard: any) => ({
            id: standard.classStandardsId,
            label: standard.classStandardName,
          }))
        );
        this.loadingAdmitClasses = false;
        if (preservedClassId) {
          this.admitForm.classId = preservedClassId;
          this.onAdmitClassChange(true, preservedSectionId);
        }
      },
      error: () => {
        this.loadingAdmitClasses = false;
      },
    });
  }

  onAdmitClassChange(preserveSelection: boolean = false, preservedSectionId: number | null = null): void {
    if (!preserveSelection) {
      this.admitForm.sectionId = null;
    }
    this.admitSections = [];
    if (!this.admitForm.categoryId || !this.admitForm.classId) {
      return;
    }
    this.loadingAdmitSections = true;
    this.admissionService.getAdmissionEnquiryClassSections([this.admitForm.categoryId], [this.admitForm.classId]).subscribe({
      next: (response: any) => {
        const categoryGroups = response?.body?.classSections ?? [];
        this.admitSections = categoryGroups.flatMap((categoryGroup: any) =>
          (categoryGroup.standards ?? []).flatMap((standard: any) =>
            (standard.sections ?? []).map((section: any) => ({
              id: section.classId,
              label: section.classDivision,
            }))
          )
        );
        this.loadingAdmitSections = false;
        if (preserveSelection && preservedSectionId) {
          this.admitForm.sectionId = preservedSectionId;
        }
      },
      error: () => {
        this.loadingAdmitSections = false;
      },
    });
  }

  formatDateForApi(date: Date | null): string {
    if (!date) {
      return '';
    }
    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    return `${day}/${month}/${date.getFullYear()}`;
  }

  showAdmitConfirm: boolean = false;

  submitAdmitStudent(): void {
    if (!this.admittingEnquiry || this.isSubmittingAdmit) {
      return;
    }
    this.admitFieldErrors = {};
    if (!this.admitForm.registrationId.trim()) {
      this.admitFieldErrors.registrationId = this.translate.instant('admissionEnquiryPage.registrationIdRequired');
    }
    if (!this.admitForm.dateOfAdmission) {
      this.admitFieldErrors.dateOfAdmission = this.translate.instant('admissionEnquiryPage.dateOfAdmissionRequired');
    }
    if (!this.admitForm.categoryId) {
      this.admitFieldErrors.categoryId = this.translate.instant('admissionEnquiryPage.categoryRequired');
    }
    if (!this.admitForm.classId) {
      this.admitFieldErrors.classId = this.translate.instant('admissionEnquiryPage.classRequired');
    }
    if (!this.admitForm.sectionId) {
      this.admitFieldErrors.sectionId = this.translate.instant('admissionEnquiryPage.sectionRequired');
    }
    if (Object.keys(this.admitFieldErrors).length > 0) {
      return;
    }

    this.showAdmitConfirm = true;
  }

  cancelAdmitConfirm(): void {
    this.showAdmitConfirm = false;
  }

  confirmAdmitStudent(): void {
    if (!this.admittingEnquiry || this.isSubmittingAdmit) {
      return;
    }

    const formData = new FormData();
    formData.append('lang', 'en');
    formData.append('enquiryId', `${this.admittingEnquiry.enquiryId}`);
    formData.append('registrationId', this.admitForm.registrationId.trim());
    formData.append('dateOfAdmission', this.formatDateForApi(this.admitForm.dateOfAdmission));
    const admissionStream = this.admitForm.admissionStream === 'general' ? '' : this.admitForm.admissionStream;
    formData.append('admissionStream', admissionStream || '');
    formData.append('categoryId', `${this.admitForm.categoryId}`);
    formData.append('classStandardsId', `${this.admitForm.classId}`);
    formData.append('classId', `${this.admitForm.sectionId}`);

    this.isSubmittingAdmit = true;
    this.admissionService.convertAdmissionEnquiry(formData).subscribe({
      next: (response: any) => {
        this.isSubmittingAdmit = false;
        if (response?.status === false || response?.body?.status === false) {
          customToastr(response?.body?.message || response?.message || this.translate.instant('admissionEnquiryPage.failedToAdmitStudent'), 'error');
          return;
        }
        customToastr(response?.body?.message || this.translate.instant('admissionEnquiryPage.studentAdmittedSuccessfully'), 'success');
        this.showAdmitConfirm = false;
        this.closeAdmitModal();
        this.closeViewEnquiryPanel();
        this.loadEnquiries();
      },
      error: (err: any) => {
        this.isSubmittingAdmit = false;
        const backendMessage = err?.error?.message || err?.error?.body?.message;
        customToastr(backendMessage || this.translate.instant('admissionEnquiryPage.failedToAdmitStudent'), 'error');
      },
    });
  }

  showEnquirySuccess: boolean = false;
  enquirySuccessMessage: string = '';
  enquirySuccessReferenceNumber: string = '';
  successAnimationOptions: AnimationOptions = {
    animationData: successAnimationData,
    loop: false,
    autoplay: true,
  };

  studentImageFile: File | null = null;
  studentImagePreview: string | null = null;
  tcDocumentFile: File | null = null;
  originalStudentImageUrl: string | null = null;
  originalTcDocumentUrl: string | null = null;
  originalTcDocumentName: string | null = null;
  originalTcDocumentType: string | null = null;
  tcDocumentRemoved: boolean = false;
  studentImageRemoved: boolean = false;

  onStudentImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) {
      return;
    }
    this.studentImageFile = file;
    const reader = new FileReader();
    reader.onload = () => {
      this.studentImagePreview = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  onTcDocumentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) {
      return;
    }
    this.tcDocumentFile = file;
  }

  removeStudentImage(): void {
    this.studentImageFile = null;
    this.studentImagePreview = null;
    if (this.originalStudentImageUrl) {
      this.studentImageRemoved = true;
      this.originalStudentImageUrl = null;
    }
  }

  removeTcDocument(): void {
    this.tcDocumentFile = null;
    if (this.originalTcDocumentUrl) {
      this.tcDocumentRemoved = true;
      this.originalTcDocumentUrl = null;
      this.originalTcDocumentName = null;
    }
  }

  private static readonly IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];

  isDocumentPreviewOpen: boolean = false;
  documentPreviewType: 'image' | 'document' | 'unsupported' | null = null;
  documentPreviewUrl: string | null = null;
  documentPreviewSafeUrl: SafeResourceUrl | null = null;
  documentPreviewLabel: string = '';

  previewTcDocument(): void {
    if (!this.tcDocumentFile) {
      return;
    }
    const objectUrl = URL.createObjectURL(this.tcDocumentFile);
    this.showDocumentPreview(objectUrl, this.tcDocumentFile.type, this.tcDocumentFile.name);
  }

  openOriginalTcDocument(): void {
    if (!this.originalTcDocumentUrl) {
      return;
    }
    this.previewDocumentUrl(this.originalTcDocumentUrl, this.originalTcDocumentType, this.originalTcDocumentName || this.translate.instant('admissionEnquiryPage.tcDocument'));
  }

  previewDocumentUrl(url: string | null | undefined, docType: string | null | undefined, label: string): void {
    if (!url) {
      return;
    }
    this.showDocumentPreview(url, docType || '', label);
  }

  private showDocumentPreview(url: string, docType: string, label: string): void {
    this.documentPreviewLabel = label;

    const normalizedType = (docType || '').toLowerCase();
    const extension = url.toLowerCase().split('?')[0].split('.').pop() || '';

    const isPdf = normalizedType === 'pdf' || normalizedType === 'application/pdf' || extension === 'pdf';
    const isImage = normalizedType === 'image' || normalizedType.startsWith('image/')
      || AdmissionInquiryComponent.IMAGE_EXTENSIONS.includes(extension);

    if (isPdf) {
      this.documentPreviewType = 'document';
      this.documentPreviewUrl = null;
      this.documentPreviewSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    } else if (isImage) {
      this.documentPreviewType = 'image';
      this.documentPreviewUrl = url;
      this.documentPreviewSafeUrl = null;
    } else {
      this.documentPreviewType = 'unsupported';
      this.documentPreviewUrl = url;
      this.documentPreviewSafeUrl = null;
    }
    this.isDocumentPreviewOpen = true;
  }

  closeDocumentPreview(): void {
    if (this.documentPreviewUrl) {
      URL.revokeObjectURL(this.documentPreviewUrl);
    }
    this.isDocumentPreviewOpen = false;
    this.documentPreviewType = null;
    this.documentPreviewUrl = null;
    this.documentPreviewSafeUrl = null;
    this.documentPreviewLabel = '';
  }

  newEnquiryForm: NewEnquiryForm = this.getEmptyEnquiryForm();

  getEmptyEnquiryForm(): NewEnquiryForm {
    return {
      firstName: '',
      lastName: '',
      dob: null,
      gender: 'Male',
      academicYearId: this.academicYears.find(y => y.isCurrent)?.id ?? null,
      categoryId: null,
      classId: null,
      sectionId: null,
      admissionType: 'Fresh',
      previousSchoolName: '',
      board: '',
      previousClassStudied: '',
      previousClassResult: '',
      reasonForTransfer: '',
      previousSchoolAddress: '',
      tcNumber: '',
      tcDate: null,
      fatherName: '',
      fatherMobile: '',
      fatherEmail: '',
      fatherAddress: '',
      motherName: '',
      motherMobile: '',
      motherEmail: '',
      motherAddress: '',
      statusId: null,
    };
  }

  openNewEnquiryModal(): void {
    this.isEditMode = false;
    this.editingEnquiryId = null;
    this.newEnquiryForm = this.getEmptyEnquiryForm();
    this.filteredClasses = [];
    this.filteredSections = [];
    this.studentImageFile = null;
    this.studentImagePreview = null;
    this.originalStudentImageUrl = null;
    this.studentImageRemoved = false;
    this.tcDocumentFile = null;
    this.originalTcDocumentUrl = null;
    this.originalTcDocumentName = null;
    this.tcDocumentRemoved = false;
    this.fieldErrors = {};
    this.showEnquirySuccess = false;
    this.showNewEnquiryModal = true;
    this.loadCategories();
  }

  private parseApiDate(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  private stripCountryCode(mobileNumber: string | null | undefined, countryCode: string | null | undefined): string {
    if (!mobileNumber) {
      return '';
    }
    if (countryCode && mobileNumber.startsWith(countryCode)) {
      return mobileNumber.slice(countryCode.length);
    }
    return mobileNumber;
  }

  openEditEnquiryPanel(enquiry: AdmissionEnquiry): void {
    const item = enquiry.raw;

    this.isEditMode = true;
    this.editingEnquiryId = enquiry.enquiryId;
    this.studentImageFile = null;
    this.studentImagePreview = item.studentImage || null;
    this.originalStudentImageUrl = item.studentImage || null;
    this.studentImageRemoved = false;
    this.tcDocumentFile = null;
    this.originalTcDocumentUrl = item.tcDocumentUrl || null;
    this.originalTcDocumentName = item.tcDocumentUrl ? item.tcNumber || this.translate.instant('admissionEnquiryPage.tcDocument') : null;
    this.originalTcDocumentType = item.tcDocumentType || null;
    this.tcDocumentRemoved = false;
    this.fieldErrors = {};
    this.showEnquirySuccess = false;

    this.newEnquiryForm = {
      firstName: item.studentFirstName ?? '',
      lastName: item.studentLastName ?? '',
      dob: this.parseApiDate(item.dateOfBirth),
      gender: (item.gender ?? '').toLowerCase() === 'female' ? 'Female' : 'Male',
      academicYearId: this.selectedAcademicYearId,
      categoryId: item.categoryId ?? null,
      classId: item.classStandardsId ?? null,
      sectionId: item.classId ?? null,
      admissionType: (item.admissionType ?? '').toUpperCase() === 'TRANSFER' ? 'Transfer' : 'Fresh',
      previousSchoolName: item.previousSchoolName ?? '',
      board: item.previousSchoolBoard ?? '',
      previousClassStudied: item.previousClassStudied ?? '',
      previousClassResult: item.previousClassResult ?? '',
      reasonForTransfer: item.reasonForTransfer ?? '',
      previousSchoolAddress: item.previousSchoolAddress ?? '',
      tcNumber: item.tcNumber ?? '',
      tcDate: this.parseApiDate(item.tcDate),
      fatherName: item.fatherName ?? '',
      fatherMobile: this.stripCountryCode(item.fatherMobileNumber, item.fatherCountryCode),
      fatherEmail: item.fatherEmailId ?? '',
      fatherAddress: item.fatherAddress ?? '',
      motherName: item.motherName ?? '',
      motherMobile: this.stripCountryCode(item.motherMobileNumber, item.motherCountryCode),
      motherEmail: item.motherEmailId ?? '',
      motherAddress: item.motherAddress ?? '',
      statusId: item.status?.statusId ?? null,
    };

    this.filteredClasses = [];
    this.filteredSections = [];
    this.showNewEnquiryModal = true;

    this.loadCategories();
    this.loadEnquiryStatuses(item.status?.statusId ?? null);
    if (item.categoryId) {
      this.loadClassStandards(item.categoryId);
    }
    if (item.categoryId && item.classStandardsId) {
      this.loadClassSections(item.categoryId, item.classStandardsId);
    }

    // The edit panel is a plain *ngIf div (not a p-dialog with an onShow
    // hook), so #ne-father-mobile/#ne-mother-mobile don't exist in the DOM
    // yet on this tick — defer until Angular renders the panel.
    setTimeout(() => this.initMobileNumberInputs());
  }

  closeNewEnquiryModal(): void {
    this.showNewEnquiryModal = false;
    this.showEnquirySuccess = false;
    this.isEditMode = false;
    this.editingEnquiryId = null;
  }

  onNewEnquiryModalShown(): void {
    this.initMobileNumberInputs();
  }

  initMobileNumberInputs(): void {
    const fatherMobileInput = $('#ne-father-mobile');
    const motherMobileInput = $('#ne-mother-mobile');

    fatherMobileInput.intlTelInput('destroy');
    motherMobileInput.intlTelInput('destroy');

    const intlTelInputOptions = {
      initialCountry: this.brand.defaultCountryIso2Lower || 'in',
      separateDialCode: false,
      autoPlaceholder: 'off',
      autoFormat: false,
      nationalMode: false,
      formatOnDisplay: false,
      utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/11.0.4/js/utils.js',
    };

    fatherMobileInput.intlTelInput(intlTelInputOptions);
    motherMobileInput.intlTelInput(intlTelInputOptions);

    fatherMobileInput.on('countrychange', () => {
      fatherMobileInput.val('');
    });

    motherMobileInput.on('countrychange', () => {
      motherMobileInput.val('');
    });
  }

  setEnquiryGender(gender: 'Male' | 'Female'): void {
    this.newEnquiryForm.gender = gender;
  }

  setAdmissionType(type: 'Fresh' | 'Transfer'): void {
    this.newEnquiryForm.admissionType = type;
  }

  resetNewEnquiryForm(): void {
    this.newEnquiryForm = this.getEmptyEnquiryForm();
    this.studentImageFile = null;
    this.studentImagePreview = null;
    this.tcDocumentFile = null;
    this.fieldErrors = {};
  }

  private validateEnquiryForm(): boolean {
    const form = this.newEnquiryForm;
    const errors: NewEnquiryFieldErrors = {};

    if (!form.firstName.trim()) {
      errors.firstName = this.translate.instant('admissionEnquiryPage.firstNameRequired');
    }
    if (!form.dob) {
      errors.dob = this.translate.instant('admissionEnquiryPage.dobRequired');
    } else {
      const ageInYears = (Date.now() - form.dob.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
      if (ageInYears < 2) {
        errors.dob = this.translate.instant('admissionEnquiryPage.studentShouldBeAtLeast2YearsOld');
      }
    }
    if (!form.gender) {
      errors.gender = this.translate.instant('admissionEnquiryPage.genderRequired');
    }
    if (!form.academicYearId) {
      errors.academicYearId = this.translate.instant('admissionEnquiryPage.academicYearRequired');
    }
    if (!form.categoryId) {
      errors.categoryId = this.translate.instant('admissionEnquiryPage.categoryRequiredNoDot');
    }
    if (!form.classId) {
      errors.classId = this.translate.instant('admissionEnquiryPage.classStandardRequired');
    }
    if (!form.fatherName.trim()) {
      errors.fatherName = this.translate.instant('admissionEnquiryPage.fathersNameRequired');
    }
    if (!this.getMobileDigits('ne-father-mobile')) {
      errors.fatherMobile = this.translate.instant('admissionEnquiryPage.mobileNumberRequired');
    } else if (!this.isValidMobileNumber('ne-father-mobile')) {
      errors.fatherMobile = this.translate.instant('admissionEnquiryPage.enterValidMobileNumber');
    }
    if (form.motherMobile.trim() && this.getMobileDigits('ne-mother-mobile') && !this.isValidMobileNumber('ne-mother-mobile')) {
      errors.motherMobile = this.translate.instant('admissionEnquiryPage.enterValidMobileNumber');
    }
    if (!form.fatherEmail.trim()) {
      errors.fatherEmail = this.translate.instant('admissionEnquiryPage.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.fatherEmail.trim())) {
      errors.fatherEmail = this.translate.instant('admissionEnquiryPage.enterValidEmailAddress');
    }
    if (!form.fatherAddress.trim()) {
      errors.fatherAddress = this.translate.instant('admissionEnquiryPage.addressRequired');
    }

    this.fieldErrors = errors;
    const errorMessages = Object.values(errors);
    if (errorMessages.length > 0) {
      customToastr(errorMessages[0], 'error');
    }
    return errorMessages.length === 0;
  }

  private toApiDateString(date: Date | null): string {
    if (!date) {
      return '';
    }
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private isIntlTelInputInitialized(input: any): boolean {
    return !!(input && input.length && typeof input.intlTelInput === 'function'
      && input.attr('data-intl-tel-input-id') != null);
  }

  private getMobileDigits(inputId: string): string {
    const input: any = $('#' + inputId);
    if (this.isIntlTelInputInitialized(input)) {
      try {
        const fullNumber = input.intlTelInput('getNumber');
        if (fullNumber) {
          return fullNumber.replace(/[^0-9]/g, '');
        }
      } catch {
        // plugin instance not ready — fall through to raw value below
      }
    }
    return (input && input.val ? input.val() : '').replace(/[^0-9]/g, '');
  }

  private getMobileCountryCode(inputId: string): string {
    const input: any = $('#' + inputId);
    if (this.isIntlTelInputInitialized(input)) {
      try {
        const countryData = input.intlTelInput('getSelectedCountryData');
        if (countryData && countryData.dialCode) {
          return countryData.dialCode;
        }
      } catch {
        // plugin instance not ready — fall through to default below
      }
    }
    return '91';
  }

  private isValidMobileNumber(inputId: string): boolean {
    const input: any = $('#' + inputId);
    if (!this.isIntlTelInputInitialized(input)) {
      return !!this.getMobileDigits(inputId);
    }
    try {
      const fullNumber = input.intlTelInput('getNumber');
      const phoneNumber = parsePhoneNumberFromString(fullNumber || '');
      return !!phoneNumber && phoneNumber.isValid();
    } catch {
      return false;
    }
  }

  submitNewEnquiry(): void {
    if (this.isEditMode) {
      this.submitEditEnquiry();
      return;
    }

    if (this.isSubmittingEnquiry) {
      return;
    }
    if (!this.validateEnquiryForm()) {
      return;
    }
    this.isSubmittingEnquiry = true;

    const form = this.newEnquiryForm;
    const formData = new FormData();
    formData.append('lang', 'en');
    formData.append('academicId', `${form.academicYearId ?? ''}`);
    formData.append('studentFirstName', form.firstName);
    formData.append('studentLastName', form.lastName);
    formData.append('dateOfBirth', this.toApiDateString(form.dob));
    formData.append('gender', form.gender.toLowerCase());
    formData.append('categoryId', `${form.categoryId ?? ''}`);
    formData.append('classStandardsId', `${form.classId ?? ''}`);
    formData.append('classId', `${form.sectionId ?? ''}`);
    formData.append('admissionType', form.admissionType.toUpperCase());
    formData.append('previousSchoolName', form.previousSchoolName);
    formData.append('previousSchoolAddress', form.previousSchoolAddress);
    formData.append('previousSchoolBoard', form.board);
    formData.append('previousClassStudied', form.previousClassStudied);
    formData.append('previousClassResult', form.previousClassResult);
    formData.append('tcNumber', form.tcNumber);
    formData.append('tcDate', this.toApiDateString(form.tcDate));
    formData.append('reasonForTransfer', form.reasonForTransfer);
    formData.append('fatherName', form.fatherName);
    formData.append('fatherMobileNumber', this.getMobileDigits('ne-father-mobile'));
    formData.append('fatherCountryCode', this.getMobileCountryCode('ne-father-mobile'));
    formData.append('fatherEmailId', form.fatherEmail);
    formData.append('fatherAddress', form.fatherAddress);
    formData.append('motherName', form.motherName);
    formData.append('motherMobileNumber', this.getMobileDigits('ne-mother-mobile'));
    formData.append('motherCountryCode', form.motherMobile ? this.getMobileCountryCode('ne-mother-mobile') : '');
    formData.append('motherEmailId', form.motherEmail);
    formData.append('motherAddress', form.motherAddress);
    if (this.studentImageFile) {
      formData.append('studentImage', this.studentImageFile);
    }
    if (this.tcDocumentFile) {
      formData.append('tcDocument', this.tcDocumentFile);
    }

    this.admissionService.addAdmissionEnquiry(formData).subscribe({
      next: (response: any) => {
        this.isSubmittingEnquiry = false;

        if (!response?.status) {
          customToastr(response?.body?.message || response?.message || this.translate.instant('admissionEnquiryPage.failedToCreateEnquiry'), 'error');
          return;
        }

        this.currentPage = 1;
        this.loadEnquiries();

        this.enquirySuccessMessage = response?.body?.message || this.translate.instant('admissionEnquiryPage.enquiryCreatedSuccessfully');
        this.enquirySuccessReferenceNumber = response?.body?.preRegistrationNumber || '';
        this.showEnquirySuccess = true;
      },
      error: (err: any) => {
        this.isSubmittingEnquiry = false;
        const backendMessage = err?.error?.body?.message || err?.error?.message;
        customToastr(backendMessage || this.translate.instant('admissionEnquiryPage.failedToCreateEnquiry'), 'error');
      },
    });
  }

  submitEditEnquiry(): void {
    if (this.isSubmittingEnquiry) {
      return;
    }
    if (!this.validateEnquiryForm()) {
      return;
    }
    if (!this.editingEnquiryId) {
      return;
    }
    this.isSubmittingEnquiry = true;

    const form = this.newEnquiryForm;
    const formData = new FormData();
    formData.append('lang', 'en');
    formData.append('academicId', `${form.academicYearId ?? ''}`);
    formData.append('enquiryId', `${this.editingEnquiryId}`);
    formData.append('statusId', `${form.statusId ?? ''}`);
    formData.append('remarks', '');
    formData.append('studentFirstName', form.firstName);
    formData.append('studentLastName', form.lastName);
    formData.append('dateOfBirth', this.toApiDateString(form.dob));
    formData.append('gender', form.gender.toLowerCase());
    formData.append('categoryId', `${form.categoryId ?? ''}`);
    formData.append('classStandardsId', `${form.classId ?? ''}`);
    formData.append('classId', `${form.sectionId ?? ''}`);
    formData.append('admissionType', form.admissionType.toUpperCase());
    formData.append('previousSchoolName', form.previousSchoolName);
    formData.append('previousSchoolAddress', form.previousSchoolAddress);
    formData.append('previousSchoolBoard', form.board);
    formData.append('previousClassStudied', form.previousClassStudied);
    formData.append('previousClassResult', form.previousClassResult);
    formData.append('tcNumber', form.tcNumber);
    formData.append('tcDate', this.toApiDateString(form.tcDate));
    formData.append('reasonForTransfer', form.reasonForTransfer);
    formData.append('fatherName', form.fatherName);
    formData.append('fatherMobileNumber', this.getMobileDigits('ne-father-mobile'));
    formData.append('fatherCountryCode', this.getMobileCountryCode('ne-father-mobile'));
    formData.append('fatherEmailId', form.fatherEmail);
    formData.append('fatherAddress', form.fatherAddress);
    formData.append('motherName', form.motherName);
    formData.append('motherMobileNumber', this.getMobileDigits('ne-mother-mobile'));
    formData.append('motherCountryCode', form.motherMobile ? this.getMobileCountryCode('ne-mother-mobile') : '');
    formData.append('motherEmailId', form.motherEmail);
    formData.append('motherAddress', form.motherAddress);

    if (this.studentImageFile) {
      formData.append('studentImageActionType', 'UPDATE');
      formData.append('studentImage', this.studentImageFile);
    } else if (this.studentImageRemoved) {
      formData.append('studentImageActionType', 'DELETE');
    } else {
      formData.append('studentImageActionType', 'NONE');
    }

    if (this.tcDocumentFile) {
      formData.append('tcActionType', 'UPDATE');
      formData.append('tcDocument', this.tcDocumentFile);
    } else if (this.tcDocumentRemoved) {
      formData.append('tcActionType', 'DELETE');
    } else {
      formData.append('tcActionType', 'NONE');
    }

    this.admissionService.updateAdmissionEnquiry(formData).subscribe({
      next: (response: any) => {
        this.isSubmittingEnquiry = false;

        if (!response?.status) {
          customToastr(response?.body?.message || response?.message || this.translate.instant('admissionEnquiryPage.failedToUpdateEnquiry'), 'error');
          return;
        }

        this.loadEnquiries();

        customToastr(response?.body?.message || this.translate.instant('admissionEnquiryPage.enquiryUpdatedSuccessfully'), 'success');
        this.closeNewEnquiryModal();
      },
      error: (err: any) => {
        this.isSubmittingEnquiry = false;
        const backendMessage = err?.error?.body?.message || err?.error?.message;
        customToastr(backendMessage || this.translate.instant('admissionEnquiryPage.failedToUpdateEnquiry'), 'error');
      },
    });
  }

  allEnquiries: AdmissionEnquiry[] = [];
  pagedEnquiries: AdmissionEnquiry[] = [];
  loadingEnquiries: boolean = false;
  recordsTotal: number = 0;
  recordsFiltered: number = 0;

  statSlugToValue: Record<string, number | string> = {};
  displayStats: DisplayStat[] = [];

  searchText: string = '';
  activeFilter: StatusFilter = 'All';
  filters = STATUS_FILTERS;

  filterCategoryId: number | null = null;
  filterClassId: number | null = null;
  filterSectionId: number | null = null;
  filterClasses: { id: number; label: string }[] = [];
  filterSections: { id: number; label: string }[] = [];
  loadingFilterClasses: boolean = false;
  loadingFilterSections: boolean = false;

  onFilterCategoryChange(): void {
    this.filterClassId = null;
    this.filterSectionId = null;
    this.filterClasses = [];
    this.filterSections = [];
    if (this.filterCategoryId) {
      this.loadingFilterClasses = true;
      this.admissionService.getAdmissionEnquiryClassStandards([this.filterCategoryId]).subscribe({
        next: (response: any) => {
          const groups = response?.body?.classStandards ?? [];
          this.filterClasses = groups.flatMap((group: any) =>
            (group.standards ?? []).map((standard: any) => ({
              id: standard.classStandardsId,
              label: standard.classStandardName,
            }))
          );
          this.loadingFilterClasses = false;
        },
        error: () => {
          this.loadingFilterClasses = false;
        },
      });
    }
    this.currentPage = 1;
    this.loadEnquiries();
  }

  onFilterClassChange(): void {
    this.filterSectionId = null;
    this.filterSections = [];
    if (this.filterCategoryId && this.filterClassId) {
      this.loadingFilterSections = true;
      this.admissionService.getAdmissionEnquiryClassSections([this.filterCategoryId], [this.filterClassId]).subscribe({
        next: (response: any) => {
          const categoryGroups = response?.body?.classSections ?? [];
          this.filterSections = categoryGroups.flatMap((categoryGroup: any) =>
            (categoryGroup.standards ?? []).flatMap((standard: any) =>
              (standard.sections ?? []).map((section: any) => ({
                id: section.classId,
                label: section.classDivision,
              }))
            )
          );
          this.loadingFilterSections = false;
        },
        error: () => {
          this.loadingFilterSections = false;
        },
      });
    }
    this.currentPage = 1;
    this.loadEnquiries();
  }

  onFilterSectionChange(): void {
    this.currentPage = 1;
    this.loadEnquiries();
  }

  pageSize: number = 20;
  currentPage: number = 1;
  totalPages: number = 1;

  readonly skeletonStatCards = Array.from({ length: 4 }, () => ({}));
  readonly skeletonRows = Array.from({ length: 5 }, () => ({}));

  private searchDebounceTimer: any = null;

  isExportingEnquiries: boolean = false;

  exportEnquiries(): void {
    if (!this.selectedAcademicYearId || this.isExportingEnquiries) {
      return;
    }
    const activeStatus = STATUS_FILTERS.find(f => f.slug === this.activeFilter);

    this.isExportingEnquiries = true;
    this.admissionService.exportAdmissionEnquiryList({
      academicId: this.selectedAcademicYearId,
      statusId: activeStatus?.statusId ?? '',
      categoryId: this.filterCategoryId ?? '',
      classStandardsId: this.filterClassId ?? '',
      classId: this.filterSectionId ?? '',
      search: this.searchText.trim(),
    }).subscribe({
      next: (response: any) => {
        this.isExportingEnquiries = false;
        if (response instanceof Blob && response.size > 0) {
          const url = window.URL.createObjectURL(response);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'ADMISSION_ENQUIRY_REPORT.xlsx';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          customToastr(this.translate.instant('admissionEnquiryPage.reportExportedSuccessfully'), 'success');
        } else {
          customToastr(this.translate.instant('admissionEnquiryPage.failedToExportReport'), 'error');
        }
      },
      error: () => {
        this.isExportingEnquiries = false;
        customToastr(this.translate.instant('admissionEnquiryPage.failedToExportReport'), 'error');
      },
    });
  }

  loadEnquiries(): void {
    if (!this.selectedAcademicYearId) {
      this.allEnquiries = [];
      this.pagedEnquiries = [];
      this.recordsTotal = 0;
      this.recordsFiltered = 0;
      this.statSlugToValue = {};
      this.displayStats = [];
      this.totalPages = 1;
      return;
    }

    const activeStatus = STATUS_FILTERS.find(f => f.slug === this.activeFilter);

    this.loadingEnquiries = true;
    this.admissionService.getAdmissionEnquiryList({
      academicId: this.selectedAcademicYearId,
      statusId: activeStatus?.statusId ?? '',
      categoryId: this.filterCategoryId ?? '',
      classStandardsId: this.filterClassId ?? '',
      classId: this.filterSectionId ?? '',
      search: this.searchText.trim(),
      pageNumber: this.currentPage,
      pageSize: this.pageSize,
    }).subscribe({
      next: (response: any) => {
        this.loadingEnquiries = false;
        const body = response?.body ?? {};
        const list = body.data ?? [];

        this.recordsTotal = body.recordsTotal ?? list.length;
        this.recordsFiltered = body.recordsFiltered ?? list.length;

        const statSlugToValue: Record<string, number | string> = {};
        (body.stats ?? []).forEach((stat: any) => {
          statSlugToValue[stat.statSlug] = stat.statValue;
        });
        this.statSlugToValue = statSlugToValue;

        this.displayStats = (body.stats ?? []).map((stat: any) => {
          const config = STAT_SLUG_CONFIG[stat.statSlug];
          const isText = typeof stat.statValue === 'string' && isNaN(Number(stat.statValue));
          const value = isText
            ? (stat.statValue || '—')
            : `${Number(stat.statValue) || 0}${config?.isPercent ? '%' : ''}`;

          return {
            slug: stat.statSlug,
            label: stat.statDescription ?? stat.statSlug,
            icon: config?.icon ?? 'ri-bar-chart-line',
            variant: config?.variant ?? 'stat-fallback',
            value,
            isText,
          };
        });

        this.allEnquiries = list.map((item: any) => ({
          enquiryId: item.enquiryId,
          studentName: `${item.studentFirstName ?? ''} ${item.studentLastName ?? ''}`.trim(),
          studentImage: item.studentImage ?? '',
          gender: item.gender ? item.gender.charAt(0).toUpperCase() + item.gender.slice(1).toLowerCase() : '',
          dob: item.dateOfBirth ?? '',
          parentName: item.fatherName ?? item.motherName ?? '',
          parentPhone: item.fatherMobileNumber ?? item.motherMobileNumber ?? '',
          className: item.classStandardName ?? '',
          section: item.classDivision ?? '',
          type: (item.admissionType ?? '').toUpperCase() === 'TRANSFER' ? 'Transfer' : 'Fresh',
          enquiryDate: item.createdDate ?? '',
          statusSlug: item.status?.slug ?? 'NEW',
          statusName: item.status?.statusName ?? 'New',
          raw: item,
        }));

        this.pagedEnquiries = this.allEnquiries;
        this.totalPages = Math.max(1, Math.ceil(this.recordsFiltered / this.pageSize));
      },
      error: () => {
        this.loadingEnquiries = false;
        customToastr(this.translate.instant('admissionEnquiryPage.failedToLoadEnquiries'), 'error');
      },
    });
  }


  permissions: any = {};

  loadPermissions(): void {
    const adminPermissionlist = localStorage.getItem('adminPermissionlist');
    if (adminPermissionlist) {
      try {
        this.permissions = JSON.parse(adminPermissionlist);
      } catch {
        this.permissions = {};
      }
    }
  }

  ngOnInit(): void {
    this.loadPermissions();
    this.loadingEnquiries = true;
    this.loadAcademicYears();
    this.loadCategories();
  }

  onAcademicYearChange(): void {
    this.currentPage = 1;
    this.loadEnquiries();
  }

  setFilter(filter: StatusFilter): void {
    this.activeFilter = filter;
    this.currentPage = 1;
    this.loadEnquiries();
  }

  onSearchChange(): void {
    this.currentPage = 1;
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => this.loadEnquiries(), 400);
  }

  get paginatorFirst(): number {
    return (this.currentPage - 1) * this.pageSize;
  }

  onPaginatorPageChange(event: any): void {
    this.currentPage = event.page + 1;
    this.pageSize = event.rows;
    this.loadEnquiries();
  }

  get resultRangeStart(): number {
    return this.recordsFiltered === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
  }

  get resultRangeEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.recordsFiltered);
  }

  getInitials(name: string): string {
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part[0].toUpperCase())
      .join('');
  }

  getStatusClass(statusSlug: string): string {
    return 'status-badge status-' + statusSlug.toLowerCase().replace(/_/g, '-');
  }

  getAvatarClass(name: string): string {
    const colors = ['avatar-blue', 'avatar-purple', 'avatar-green', 'avatar-orange'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  }
}
